/**
 * Group Membership Report Page
 *
 * Admin report showing current group memberships across all rider groups
 * - Filter by group and rider type
 * - Text search by name or group
 * - Summary statistics with per-group counts
 * - Sortable table
 * - Export to CSV
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 3.1, 3.2, 3.3, 3.4,
 *   4.1–4.3, 5.1–5.3, 6.1–6.4, 7.1–7.4, 8.1–8.5, 9.1–9.4, 10.1, 10.2
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient, Rider, Group } from '../../api/rest-client';
import { Button } from '../../components/common/Button';
import { formatRiderName } from '../../utils/riderDisplay';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface GroupMembershipRow {
  rider_id: string;
  name: string;
  type: string;
  grade: string;
  category: string;
  group: string;
}

export interface GroupedRiderData {
  groupName: string;
  riders: GroupMembershipRow[];
}

// ---------------------------------------------------------------------------
// Pure utility functions (exported for property-based testing)
// ---------------------------------------------------------------------------

/**
 * Determine whether a rider is active (not disabled).
 */
export function isActiveRider(rider: Rider): boolean {
  return rider.disabled !== '1' && rider.disabled !== 1;
}

/**
 * Get the current season (current year as string).
 */
export function getCurrentSeason(): string {
  return new Date().getFullYear().toString();
}

/**
 * Determine whether a rider belongs to the given season.
 * A rider is in the season if their primary `season` matches
 * OR their `seasons_active` array includes the season.
 */
export function isRiderInSeason(rider: Rider, season: string): boolean {
  return rider.season === season ||
    (Array.isArray(rider.seasons_active) && rider.seasons_active.includes(season));
}

/**
 * Filter an array of riders to only active ones.
 */
export function filterActiveRiders(riders: Rider[]): Rider[] {
  return riders.filter(isActiveRider);
}

/**
 * Transform an API Rider object into a GroupMembershipRow with safe defaults.
 */
export function transformRider(rider: Rider): GroupMembershipRow {
  const rawType = rider.type || 'Student';
  return {
    rider_id: rider.rider_id || '',
    name: rider.name,
    type: rawType === 'Rider' ? 'Student' : rawType,
    grade: rider.grade || '',
    category: rider.category || '',
    group: rider.group || '',
  };
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * Apply group, type, and search filters to a riders array.
 *
 * Pure function exported for property-based testing.
 *
 * All filters combine as intersection (AND logic):
 * - groupFilter empty → all groups pass
 * - typeFilter empty → all types pass
 * - searchTerm empty → all riders pass search
 * - searchTerm matches case-insensitively against rider name or group name
 *
 * Requirements: 4.1, 4.2, 4.3, 5.1, 5.2, 5.3, 6.1, 6.2, 6.3, 6.4
 */
export function applyFilters(
  riders: GroupMembershipRow[],
  groupFilter: string,
  typeFilter: string,
  searchTerm: string,
  gradeFilter: string = '',
  categoryFilter: string = '',
): GroupMembershipRow[] {
  let filtered = riders;

  if (groupFilter) {
    filtered = filtered.filter((r) => r.group === groupFilter);
  }

  if (typeFilter) {
    filtered = filtered.filter((r) => r.type === typeFilter);
  }

  if (gradeFilter) {
    filtered = filtered.filter((r) => r.grade === gradeFilter);
  }

  if (categoryFilter) {
    filtered = filtered.filter((r) => r.category === categoryFilter);
  }

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        r.group.toLowerCase().includes(term),
    );
  }

  return filtered;
}

// ---------------------------------------------------------------------------
// Statistics
// ---------------------------------------------------------------------------

export interface GroupStatEntry {
  groupName: string;
  count: number;
  order: number;
}

export interface GroupMembershipStatistics {
  totalGroups: number;
  totalActiveRiders: number;
  perGroupCounts: GroupStatEntry[];
}

/**
 * Compute summary statistics from riders and groups.
 *
 * Pure function exported for property-based testing.
 *
 * - totalGroups: number of distinct groups provided
 * - totalActiveRiders: length of the riders array
 * - perGroupCounts: one entry per group (sorted by group_order ascending,
 *   1 = fastest) plus an "Unassigned" entry for riders with empty/missing group.
 *   Only groups that have at least one rider OR exist in the groups list are included.
 *
 * Requirements: 3.1, 3.3, 3.4, 8.4
 */
export function getStatistics(
  riders: GroupMembershipRow[],
  groups: Group[],
): GroupMembershipStatistics {
  // Count riders per group name
  const countMap: Record<string, number> = {};
  let unassignedCount = 0;

  for (const rider of riders) {
    if (!rider.group) {
      unassignedCount += 1;
    } else {
      countMap[rider.group] = (countMap[rider.group] || 0) + 1;
    }
  }

  // Build a lookup from group name → order
  const orderMap: Record<string, number> = {};
  for (const g of groups) {
    orderMap[g.name] = g.order ?? 999;
  }

  // Build per-group entries: include every group from the groups list,
  // plus any rider group names that aren't in the groups list.
  const seen = new Set<string>();
  const entries: GroupStatEntry[] = [];

  for (const g of groups) {
    seen.add(g.name);
    entries.push({
      groupName: g.name,
      count: countMap[g.name] || 0,
      order: g.order ?? 999,
    });
  }

  // Add any rider groups not in the groups list
  for (const name of Object.keys(countMap)) {
    if (!seen.has(name)) {
      entries.push({
        groupName: name,
        count: countMap[name],
        order: orderMap[name] ?? 999,
      });
    }
  }

  // Sort by order ascending (1 = fastest first)
  entries.sort((a, b) => a.order - b.order);

  // Append "Unassigned" at the end if there are any
  if (unassignedCount > 0) {
    entries.push({
      groupName: 'Unassigned',
      count: unassignedCount,
      order: Infinity,
    });
  }

  return {
    totalGroups: groups.length,
    totalActiveRiders: riders.length,
    perGroupCounts: entries,
  };
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

export type SortColumn = 'name' | 'type' | 'grade' | 'category' | 'group';
export type SortDirection = 'asc' | 'desc';

/**
 * Sort an array of GroupMembershipRow by the given column and direction.
 *
 * Pure function exported for property-based testing.
 *
 * Returns a new sorted array (does not mutate the input).
 * Sorting is lexicographic using `localeCompare`.
 *
 * Requirements: 7.1, 7.2, 7.3
 */
export function sortRiders(
  riders: GroupMembershipRow[],
  column: SortColumn,
  direction: SortDirection,
): GroupMembershipRow[] {
  return [...riders].sort((a, b) => {
    const aVal = a[column] || '';
    const bVal = b[column] || '';
    return direction === 'asc'
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  });
}

// ---------------------------------------------------------------------------
// CSV Export
// ---------------------------------------------------------------------------

/**
 * Generate CSV string content from an array of GroupMembershipRow.
 *
 * Pure function exported for property-based testing.
 *
 * Returns a string with header line `Name,Type,Grade,Category,Group`
 * followed by one line per rider, each field double-quoted.
 *
 * Requirements: 9.2, 9.3
 */
export function generateCSVContent(riders: GroupMembershipRow[]): string {
  const headers = ['Name', 'Type', 'Grade', 'Category', 'Group'];
  return [
    headers.join(','),
    ...riders.map((r) =>
      [
        `"${r.name}"`,
        `"${r.type}"`,
        `"${r.grade}"`,
        `"${r.category}"`,
        `"${r.group}"`,
      ].join(','),
    ),
  ].join('\n');
}

/**
 * Generate the CSV export filename for a given date.
 *
 * Pure function exported for property-based testing.
 *
 * Returns `group-membership-report-YYYY-MM-DD.csv`.
 *
 * Requirements: 9.4
 */
export function generateCSVFilename(date: Date): string {
  return `group-membership-report-${date.toISOString().split('T')[0]}.csv`;
}

// ---------------------------------------------------------------------------
// Group-by-group layout helper
// ---------------------------------------------------------------------------

/**
 * Group filtered riders by their group name, sorted by group_order ascending.
 * Riders within each group are sorted alphabetically by name.
 * Riders with no group are placed in an "Unassigned" group at the end.
 *
 * Pure function exported for testing.
 */
export function groupRidersByGroup(
  riders: GroupMembershipRow[],
  groups: Group[],
): GroupedRiderData[] {
  // Build order lookup and group leader lookup
  const orderMap: Record<string, number> = {};
  const leaderMap: Record<string, string> = {};
  for (const g of groups) {
    orderMap[g.name] = g.order ?? 999;
    if (g.group_leader) {
      leaderMap[g.name] = g.group_leader;
    }
  }

  // Bucket riders by group
  const buckets: Record<string, GroupMembershipRow[]> = {};
  const unassigned: GroupMembershipRow[] = [];

  for (const r of riders) {
    if (!r.group) {
      unassigned.push(r);
    } else {
      if (!buckets[r.group]) buckets[r.group] = [];
      buckets[r.group].push(r);
    }
  }

  // Build result sorted by group_order, leaders first within each group
  const result: GroupedRiderData[] = Object.keys(buckets)
    .sort((a, b) => (orderMap[a] ?? 999) - (orderMap[b] ?? 999))
    .map((groupName) => ({
      groupName,
      riders: buckets[groupName].sort((a, b) => {
        const leaderId = leaderMap[groupName];
        const aIsLeader = leaderId && a.rider_id === leaderId;
        const bIsLeader = leaderId && b.rider_id === leaderId;
        if (aIsLeader && !bIsLeader) return -1;
        if (!aIsLeader && bIsLeader) return 1;
        return a.name.localeCompare(b.name);
      }),
    }));

  // Append unassigned at the end
  if (unassigned.length > 0) {
    result.push({
      groupName: 'Unassigned',
      riders: unassigned.sort((a, b) => a.name.localeCompare(b.name)),
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Display ordering for Grade and Category dropdowns
// ---------------------------------------------------------------------------

const GRADE_ORDER = ['12th', '11th', '10th', '9th', '8th', '7th', '6th'];
const CATEGORY_ORDER = ['Varsity', 'JV1', 'JV2', 'Freshman', '8th Grade', '7th Grade', '6th Grade'];

/**
 * Sort values by a predefined order. Values not in the order list go at the end alphabetically.
 */
function sortByOrder(values: string[], order: string[]): string[] {
  const orderMap = new Map(order.map((v, i) => [v, i]));
  return [...values].sort((a, b) => {
    const ai = orderMap.get(a) ?? Infinity;
    const bi = orderMap.get(b) ?? Infinity;
    if (ai !== Infinity && bi !== Infinity) return ai - bi;
    if (ai !== Infinity) return -1;
    if (bi !== Infinity) return 1;
    return a.localeCompare(b);
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GroupMembershipReport: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [riders, setRiders] = useState<GroupMembershipRow[]>([]);
  const [filteredRiders, setFilteredRiders] = useState<GroupMembershipRow[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [groupFilter, setGroupFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [seasonFilter, setSeasonFilter] = useState<'current' | 'all'>('current');

  // Keep raw riders for re-filtering when season changes
  const [allActiveRiders, setAllActiveRiders] = useState<Rider[]>([]);
  useEffect(() => {
    loadData();
  }, []);

  // Re-apply season filter when seasonFilter changes
  useEffect(() => {
    const currentSeason = getCurrentSeason();
    let ridersToTransform = allActiveRiders;
    if (seasonFilter === 'current') {
      ridersToTransform = allActiveRiders.filter((r) => isRiderInSeason(r, currentSeason));
    }
    const transformedRiders = ridersToTransform.map(transformRider);
    transformedRiders.sort((a, b) => a.name.localeCompare(b.name));
    setRiders(transformedRiders);
  }, [allActiveRiders, seasonFilter]);

  useEffect(() => {
    runFilters();
  }, [riders, groupFilter, typeFilter, gradeFilter, categoryFilter, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch riders and groups in parallel
      const [allRiders, groupsData] = await Promise.all([
        restClient.getRiders(),
        restClient.getGroups(),
      ]);

      // Filter active riders and store raw for season re-filtering
      const activeRiders = filterActiveRiders(allRiders);
      setAllActiveRiders(activeRiders);

      // Sort groups by order ascending (1 = fastest)
      const sortedGroups = [...groupsData].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setGroups(sortedGroups);
    } catch (err) {
      setError('Failed to load group membership data');
    } finally {
      setLoading(false);
    }
  };

  const runFilters = () => {
    setFilteredRiders(applyFilters(riders, groupFilter, typeFilter, searchTerm, gradeFilter, categoryFilter));
  };

  const exportToCSV = useCallback(() => {
    if (filteredRiders.length === 0) {
      alert('No data to export');
      return;
    }

    const csvContent = generateCSVContent(filteredRiders);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', generateCSVFilename(new Date()));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredRiders]);

  // Calculate statistics using the exported pure function
  const statistics = getStatistics(riders, groups);

  // Group riders by their group name for the card layout
  const groupedRiders = groupRidersByGroup(filteredRiders, groups);

  // Build group leader lookup: group_name → group_leader rider_id
  const groupLeaderMap: Record<string, string> = {};
  for (const g of groups) {
    if (g.group_leader) {
      groupLeaderMap[g.name] = g.group_leader;
    }
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading group membership data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold">Group Membership Report</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/admin/reports')}>
            <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Reports
          </Button>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 min-h-[44px]"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
        <div className="flex flex-wrap gap-4 text-sm">
          <span><strong>Groups:</strong> {statistics.totalGroups}</span>
          <span><strong>Active Riders:</strong> {statistics.totalActiveRiders}</span>
          <span><strong>Showing:</strong> {filteredRiders.length}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <select
          value={seasonFilter}
          onChange={(e) => setSeasonFilter(e.target.value as 'current' | 'all')}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by season"
        >
          <option value="current">Current Season</option>
          <option value="all">All Seasons</option>
        </select>
        <select
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by group"
        >
          <option value="">All Groups</option>
          {groups.map((group) => (
            <option key={group.group_id} value={group.name}>
              {group.name}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by type"
        >
          <option value="">All Types</option>
          <option value="Student">Student</option>
          <option value="Coach">Coach</option>
        </select>
        <select
          value={gradeFilter}
          onChange={(e) => setGradeFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by grade"
        >
          <option value="">All Grades</option>
          {sortByOrder([...new Set(riders.map((r) => r.grade).filter(Boolean))], GRADE_ORDER).map((grade) => (
            <option key={grade} value={grade}>{grade}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px] bg-white"
          aria-label="Filter by category"
        >
          <option value="">All Categories</option>
          {sortByOrder([...new Set(riders.map((r) => r.category).filter(Boolean))], CATEGORY_ORDER).map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name..."
          className="w-full px-3 py-2 border border-gray-300 rounded min-h-[44px]"
          aria-label="Search riders"
        />
        <button
          onClick={() => {
            setSeasonFilter('current');
            setGroupFilter('');
            setTypeFilter('');
            setGradeFilter('');
            setCategoryFilter('');
            setSearchTerm('');
          }}
          className="w-full px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 min-h-[44px]"
        >
          Clear Filters
        </button>
      </div>

      {/* Group cards */}
      {groupedRiders.length === 0 ? (
        <div className="text-center text-gray-500 py-8">No riders found</div>
      ) : (
        <div className="space-y-4">
          {groupedRiders.map((groupData) => (
            <div key={groupData.groupName} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Group header */}
              <div className="bg-gray-100 px-4 py-3 flex justify-between items-center">
                <h2 className="text-lg font-semibold">{groupData.groupName}</h2>
                <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded">
                  {groupData.riders.length} {groupData.riders.length === 1 ? 'rider' : 'riders'}
                </span>
              </div>
              {/* Rider list */}
              <ul className="divide-y divide-gray-100">
                {groupData.riders.map((rider, idx) => (
                  <li key={idx} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                    <span className="font-medium flex-1">
                      {formatRiderName(rider)}
                      {rider.rider_id && groupLeaderMap[groupData.groupName] === rider.rider_id ? ' ⭐' : ''}
                    </span>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                      {rider.type && <span className="bg-gray-100 px-2 py-0.5 rounded">{rider.type}</span>}
                      {rider.grade && <span className="bg-gray-100 px-2 py-0.5 rounded">{rider.grade}</span>}
                      {rider.category && <span className="bg-gray-100 px-2 py-0.5 rounded">{rider.category}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupMembershipReport;
